import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiBook, FiBookOpen, FiClock, FiAlertCircle, 
  FiArrowRight, FiTrendingUp, FiStar, FiBell,
  FiCheckCircle, FiXCircle, FiMessageSquare
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Home.css';

const Home = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBorrowed: 0,
    activeBorrowings: 0,
    dueSoon: 0,
    overdue: 0
  });
  const [newBooks, setNewBooks] = useState([]);
  const [popularBooks, setPopularBooks] = useState([]);
  const [myBorrowings, setMyBorrowings] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user stats
        const statsRes = await api.getUserStats(user?.id);
        if (statsRes.success) {
          setStats(statsRes.data);
        }

        // Fetch new books
        const newBooksRes = await api.getNewBooks(6);
        if (newBooksRes.success) {
          setNewBooks(newBooksRes.data || []);
        }

        // Fetch popular books
        const popularRes = await api.getPopularBooks(4);
        if (popularRes.success) {
          setPopularBooks(popularRes.data || []);
        }

        // Fetch my borrowings
        const borrowRes = await api.getMyBorrowings(user?.id);
        if (borrowRes.success) {
          setMyBorrowings((borrowRes.data || []).slice(0, 3));
        }

        // Fetch notifications (sitasi responses for books and journals)
        const notifRes = await fetch(`/api/notifications.php?user_id=${user.id}&role=user`);
        const notifData = await notifRes.json();
        if (notifData.success) {
          // Filter only sitasi notifications (books and journals)
          const sitasiNotifs = (notifData.data || []).filter(n => 
            n.id.startsWith('sitasi_approved') || n.id.startsWith('sitasi_rejected') ||
            n.id.startsWith('sitasi_jurnal_approved') || n.id.startsWith('sitasi_jurnal_rejected')
          );
          setNotifications(sitasiNotifs);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const borrowLimit = user?.role === 'dosen' ? 5 : 3;
  const borrowDays = user?.role === 'dosen' ? 14 : 7;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Selamat Pagi';
    if (hour < 17) return 'Selamat Siang';
    return 'Selamat Malam';
  };

  if (loading) {
    return (
      <div className="home__loading">
        <div className="home__loading-spinner"></div>
        <p>Memuat data...</p>
      </div>
    );
  }

  return (
    <div className="home">
      {/* Welcome Section */}
      <section className="home__welcome">
        <div className="home__welcome-content">
          <h1 className="home__welcome-title">
            {getGreeting()}, <span>{user?.nama?.split(' ')[0] || 'User'}</span>! 👋
          </h1>
          <p className="home__welcome-subtitle">
            Anda dapat meminjam hingga <strong>{borrowLimit} buku</strong> dengan 
            durasi <strong>{borrowDays} hari</strong> per buku.
          </p>
        </div>
        <div className="home__welcome-illustration">
          <FiBook className="home__welcome-icon" />
        </div>
      </section>

      {/* Stats Cards */}
      <section className="home__stats">
        <div className="home__stat-card home__stat-card--primary">
          <div className="home__stat-icon">
            <FiBookOpen />
          </div>
          <div className="home__stat-content">
            <span className="home__stat-value">{stats.activeBorrowings || 0}</span>
            <span className="home__stat-label">Sedang Dipinjam</span>
          </div>
        </div>

        <div className="home__stat-card home__stat-card--warning">
          <div className="home__stat-icon">
            <FiClock />
          </div>
          <div className="home__stat-content">
            <span className="home__stat-value">{stats.dueSoon || 0}</span>
            <span className="home__stat-label">Jatuh Tempo</span>
          </div>
        </div>

        <div className="home__stat-card home__stat-card--danger">
          <div className="home__stat-icon">
            <FiAlertCircle />
          </div>
          <div className="home__stat-content">
            <span className="home__stat-value">{stats.overdue || 0}</span>
            <span className="home__stat-label">Terlambat</span>
          </div>
        </div>

        <div className="home__stat-card home__stat-card--success">
          <div className="home__stat-icon">
            <FiBook />
          </div>
          <div className="home__stat-content">
            <span className="home__stat-value">{stats.totalBorrowed || 0}</span>
            <span className="home__stat-label">Total Pinjaman</span>
          </div>
        </div>
      </section>

      {/* Sitasi Notifications */}
      {notifications.length > 0 && (
        <section className="home__notifications">
          <div className="home__notifications-header">
            <h2><FiBell /> Notifikasi Request Sitasi</h2>
          </div>
          <div className="home__notifications-list">
            {notifications.map(notif => (
              <div 
                key={notif.id} 
                className={`home__notification-card home__notification-card--${notif.type}`}
              >
                <div className="home__notification-icon">
                  {notif.type === 'success' ? <FiCheckCircle /> : <FiXCircle />}
                </div>
                <div className="home__notification-content">
                  <h4 className="home__notification-title">{notif.title}</h4>
                  <p className="home__notification-message">{notif.message}</p>
                  {notif.catatan && (
                    <div className="home__notification-note">
                      <FiMessageSquare />
                      <span><strong>Catatan:</strong> {notif.catatan}</span>
                    </div>
                  )}
                  <span className="home__notification-time">{notif.time}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Main Content Grid */}
      <div className="home__grid">
        {/* New Books */}
        <section className="home__section home__section--books">
          <div className="home__section-header">
            <h2 className="home__section-title">
              <FiTrendingUp /> Buku Terbaru
            </h2>
            <Link to="/katalog?sort=newest" className="home__section-link">
              Lihat Semua <FiArrowRight />
            </Link>
          </div>
          
          <div className="home__books-grid">
            {newBooks.length > 0 ? (
              newBooks.map(book => (
                <Link 
                  to={`/katalog/${book.id_buku}`} 
                  key={book.id_buku} 
                  className="home__book-card"
                >
                  <div className="home__book-cover">
                    <img 
                      src={book.cover_image || '/images/cover-default.png'} 
                      alt={book.judul}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/cover-default.png';
                      }}
                    />
                  </div>
                  <div className="home__book-info">
                    <h4 className="home__book-title">{book.judul}</h4>
                    <p className="home__book-author">{book.penulis || 'Unknown'}</p>
                  </div>
                </Link>
              ))
            ) : (
              <p className="home__empty">Belum ada buku terbaru</p>
            )}
          </div>
        </section>

        {/* Sidebar */}
        <aside className="home__sidebar">
          {/* My Active Borrowings */}
          <div className="home__sidebar-card">
            <div className="home__sidebar-header">
              <h3><FiBookOpen /> Pinjaman Aktif</h3>
              <Link to="/peminjaman">Semua</Link>
            </div>
            <div className="home__sidebar-content">
              {myBorrowings.length > 0 ? (
                myBorrowings.map(item => (
                  <div key={item.id} className="home__borrowing-item">
                    <div className="home__borrowing-info">
                      <span className="home__borrowing-title">{item.judul}</span>
                      <span className="home__borrowing-date">
                        Jatuh tempo: {new Date(item.tanggal_kembali).toLocaleDateString('id-ID')}
                      </span>
                    </div>
                    <span className={`home__borrowing-status home__borrowing-status--${item.status}`}>
                      {item.status === 'dipinjam' ? 'Aktif' : item.status}
                    </span>
                  </div>
                ))
              ) : (
                <p className="home__sidebar-empty">Tidak ada pinjaman aktif</p>
              )}
            </div>
          </div>

          {/* Popular Books */}
          <div className="home__sidebar-card">
            <div className="home__sidebar-header">
              <h3><FiStar /> Buku Populer</h3>
              <Link to="/katalog?sort=popular">Semua</Link>
            </div>
            <div className="home__sidebar-content">
              {popularBooks.length > 0 ? (
                popularBooks.map((book, index) => (
                  <Link 
                    to={`/katalog/${book.id_buku}`} 
                    key={book.id_buku} 
                    className="home__popular-item"
                  >
                    <span className="home__popular-rank">{index + 1}</span>
                    <div className="home__popular-info">
                      <span className="home__popular-title">{book.judul}</span>
                      <span className="home__popular-author">{book.penulis}</span>
                    </div>
                  </Link>
                ))
              ) : (
                <p className="home__sidebar-empty">Belum ada data</p>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Home;
